import { filterClasses } from "@swivl-mono/helpers";
import { APIScope, FeatureFlagKey, LocationVarKey, MemberInterface } from "@swivl-mono/types";
import { Popover, PopoverPosition } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { BsPersonFillGear } from "react-icons/bs";
import { CgClose } from "react-icons/cg";
import { FaRegStar } from "react-icons/fa";
import { FiInbox, FiMessageSquare, FiPhone, FiSettings, FiTool, FiUsers } from "react-icons/fi";
import { RiChatSmileAiLine, RiPriceTag3Line } from "react-icons/ri";
import { TbBuildingSkyscraper } from "react-icons/tb";
import { useLocation, useNavigate } from "react-router-dom";

import { ShowIf } from "../../../Components/ShowIf/ShowIf";
import { facilityVarKeys } from "../../../Helpers/FeatureFlags/facilityVarKeys";
import { isFeatureEnabled } from "../../../Helpers/FeatureFlags/isFeatureEnabled";
import { useLocationVariables } from "../../../Hooks/useLocationVariables";
import AccountModel from "../../../Models/Account/AccountModel";
import { Member } from "../../../Models/Member/Member.Current";
import { NavDrawerModel } from "../../../Models/NavDrawer/NavDrawer.Model";
import "./NavDrawer.scss";
import { OrgPicker } from "./OrgPicker";

export function NavDrawer(): React.ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen } = NavDrawerModel.useState((s) => s);
  const { memberships, member } = AccountModel.useState((s) => s);
  // ENG-3198: the drawer renders for every org on both stacks — pick the
  // facility keys for the org's own stack (Cortex for Snowy, legacy otherwise).
  const facilityKeys = facilityVarKeys();
  const orgVars = useLocationVariables([facilityKeys.name, facilityKeys.imageURL]);
  const [isOrgPickerOpen, setIsOrgPickerOpen] = useState(false);

  const hasMultipleOrgs = memberships && memberships.length > 1;

  // Reset org picker when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setIsOrgPickerOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        if (isOrgPickerOpen) {
          setIsOrgPickerOpen(false);
        } else {
          NavDrawerModel.close();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isOrgPickerOpen]);

  const legacyHidden = isFeatureEnabled(FeatureFlagKey.HideLegacyNav) && isFeatureEnabled(FeatureFlagKey.Snowy);

  const isActive = (path: string): boolean => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleNav = (path: string): void => {
    NavDrawerModel.close();
    navigate(path);
  };

  const handleOrgSelect = (membership: MemberInterface): void => {
    if (membership.org.id === member?.orgId) {
      setIsOrgPickerOpen(false);
      return;
    }
    setIsOrgPickerOpen(false);
    NavDrawerModel.close();
    AccountModel.selectMember(membership);
    window.location.reload();
  };

  const contextWarning =
    import.meta.env.VITE_CONTEXT === "production" ? null : (
      <div className="NavDrawer-env">
        <span>{import.meta.env.VITE_CONTEXT}</span>
      </div>
    );

  return (
    <>
      <div className={isOpen ? "NavDrawer-backdrop isOpen" : "NavDrawer-backdrop"} onClick={() => NavDrawerModel.close()} />

      <nav className={isOpen ? "NavDrawer isOpen" : "NavDrawer"}>
        {/* Header */}
        <div className="NavDrawer-header">
          <img src="/img/logo/SwivlStudioWritten.png" alt="swivl" className="NavDrawer-logo" />
          <button className="NavDrawer-close" onClick={() => NavDrawerModel.close()} aria-label="Close menu">
            <CgClose size={16} />
          </button>
        </div>

        {/* Nav items */}
        <div className="NavDrawer-items">
          <ShowIf scope={APIScope.USER_CRM_VIEW}>
            <button className={filterClasses("NavDrawer-item", isActive("/crm") && "isActive")} onClick={() => handleNav("/crm")}>
              <FiUsers size={16} />
              <span>CRM</span>
            </button>
          </ShowIf>

          {!legacyHidden && (
            <ShowIf scope={APIScope.CONVERSATION_VIEW}>
              <button
                className={filterClasses("NavDrawer-item", isActive("/contact-center") && !isActive("/contact-center-v3") && "isActive")}
                onClick={() => handleNav("/contact-center")}
              >
                <FiPhone size={16} />
                <span>Contact Center</span>
              </button>
            </ShowIf>
          )}

          {isFeatureEnabled(FeatureFlagKey.Snowy) && (
            <button
              className={filterClasses("NavDrawer-item", isActive("/contact-center-v3") && "isActive")}
              onClick={() => handleNav("/contact-center-v3")}
            >
              <FiPhone size={16} />
              <span>{legacyHidden ? "Contact Center" : "Contact Center - NEW"}</span>
            </button>
          )}

          {isFeatureEnabled(FeatureFlagKey.Snowy) && Member.isSwivlStaff && (
            <button className={filterClasses("NavDrawer-item", isActive("/ai-studio") && "isActive")} onClick={() => handleNav("/ai-studio")}>
              <FiMessageSquare size={16} />
              <span>AI Studio</span>
            </button>
          )}

          {isFeatureEnabled(FeatureFlagKey.Ticketing) && (
            <button className={filterClasses("NavDrawer-item", isActive("/inbox") && "isActive")} onClick={() => handleNav("/inbox")}>
              <FiInbox size={16} />
              <span>Ticketing</span>
            </button>
          )}

          {!legacyHidden && (
            <ShowIf scope={APIScope.LOCATIONS_ALL_VIEW}>
              <button
                className={filterClasses("NavDrawer-item", isActive("/properties") && !isActive("/properties-v3") && "isActive")}
                onClick={() => handleNav("/properties")}
              >
                <TbBuildingSkyscraper size={16} />
                <span>Property Manager</span>
              </button>
            </ShowIf>
          )}

          {isFeatureEnabled(FeatureFlagKey.Snowy) && (
            <ShowIf scope={APIScope.LOCATIONS_ALL_VIEW}>
              <button
                className={filterClasses("NavDrawer-item", isActive("/properties-v3") && "isActive")}
                onClick={() => handleNav("/properties-v3")}
              >
                <TbBuildingSkyscraper size={16} />
                <span>{legacyHidden ? "Property Manager" : "Property Manager - NEW"}</span>
              </button>
            </ShowIf>
          )}

          <ShowIf scope={APIScope.REVIEWS_EDIT}>
            <button className={filterClasses("NavDrawer-item", isActive("/reviews") && "isActive")} onClick={() => handleNav("/reviews")}>
              <FaRegStar size={16} />
              <span>Reviews</span>
            </button>
          </ShowIf>

          {isFeatureEnabled(FeatureFlagKey.Snowy) && (
            <ShowIf scope={APIScope.WIDGETS_AND_BRANDS_EDITOR}>
              <button
                className={filterClasses("NavDrawer-item", (isActive("/widgets") || isActive("/ctas") || isActive("/install")) && "isActive")}
                onClick={() => handleNav("/widgets")}
              >
                <RiChatSmileAiLine size={16} />
                <span>Widgets</span>
              </button>
            </ShowIf>
          )}

          {isFeatureEnabled(FeatureFlagKey.Snowy) && (
            <ShowIf scope={APIScope.WIDGETS_AND_BRANDS_EDITOR}>
              <button className={filterClasses("NavDrawer-item", isActive("/brands") && "isActive")} onClick={() => handleNav("/brands")}>
                <RiPriceTag3Line size={16} />
                <span>Brands</span>
              </button>
            </ShowIf>
          )}

          <button className={filterClasses("NavDrawer-item", isActive("/settings") && "isActive")} onClick={() => handleNav("/settings/analytics")}>
            <FiSettings size={16} />
            <span>Settings</span>
          </button>

          {Member.isSwivlStaff && (
            <button className={filterClasses("NavDrawer-item", isActive("/admin") && "isActive")} onClick={() => handleNav("/admin")}>
              <BsPersonFillGear size={16} />
              <span>Admin</span>
            </button>
          )}

          {/* Policy Builder — local dev only (route is import.meta.env.DEV-gated in Router.tsx;
              DEV is statically false in production builds, so this entry is compiled out). */}
          {import.meta.env.DEV && (
            <button
              className={filterClasses("NavDrawer-item", isActive("/policy-builder") && "isActive")}
              onClick={() => handleNav("/policy-builder")}
            >
              <FiTool size={16} />
              <span>Policy Builder</span>
            </button>
          )}
        </div>

        {/* Bottom section */}
        <div className="NavDrawer-bottom">
          {contextWarning}

          {orgVars && (
            <div className="NavDrawer-account">
              {hasMultipleOrgs ? (
                <Popover
                  isOpen={isOrgPickerOpen}
                  onOpenChange={setIsOrgPickerOpen}
                  side="top"
                  position={PopoverPosition.LEFT}
                  onEscapeKeyDown={(e) => e.stopPropagation()}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  trigger={
                    <button className="NavDrawer-account-trigger" aria-label="Switch organization">
                      {orgVars[facilityKeys.imageURL] && (
                        <div className="NavDrawer-account-avatar">
                          <img src={orgVars[facilityKeys.imageURL]} alt="Organization" />
                        </div>
                      )}
                      <div className="NavDrawer-account-info">
                        <div className="NavDrawer-account-name">{orgVars[facilityKeys.name]}</div>
                      </div>
                      <div className={isOrgPickerOpen ? "NavDrawer-account-switch isOpen" : "NavDrawer-account-switch"}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M4 10l4-4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </button>
                  }
                >
                  <OrgPicker memberships={memberships!} currentOrgId={member?.orgId ?? 0} isOpen={isOrgPickerOpen} onSelect={handleOrgSelect} />
                </Popover>
              ) : (
                <div className="NavDrawer-account-row">
                  {orgVars[facilityKeys.imageURL] && (
                    <div className="NavDrawer-account-avatar">
                      <img src={orgVars[facilityKeys.imageURL]} alt="Organization" />
                    </div>
                  )}
                  <div className="NavDrawer-account-info">
                    <div className="NavDrawer-account-name">{orgVars[facilityKeys.name]}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
